import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

function ago(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / (60 * 24))}d ago`;
}

// A muted swatch per event family, keyed off the prefix before the dot.
const FAMILY_COLOR: Record<string, string> = {
  permission: "#7a5aa8",
  legal_hold: "#c0612f",
  export: "#2a6f8a",
  member: "#b23b6f",
  ticket: "#006747",
  incident: "#c0612f",
};
const familyOf = (event: string) => event.split(".")[0] ?? event;

export function Audit() {
  const [event, setEvent] = useState<string | undefined>(undefined);
  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", event ?? null],
    queryFn: () => api.audit({ event }),
  });

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Audit log</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Immutable record of privileged actions — who did what, to which record, from where. Append-only.
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          onClick={() => setEvent(undefined)}
          style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: "4px 11px", cursor: "pointer", border: "1px solid #e7e5df", background: event === undefined ? "#006747" : "#fff", color: event === undefined ? "#fff" : "#4a504a" }}
        >
          All events
        </button>
        {data.eventTypes.map((et) => (
          <button
            key={et}
            onClick={() => setEvent(et)}
            style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: "4px 11px", cursor: "pointer", border: "1px solid #e7e5df", background: event === et ? "#006747" : "#fff", color: event === et ? "#fff" : "#4a504a", fontFamily: "'Space Mono',monospace" }}
          >
            {et}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 760 }}>
            <div style={{ display: "grid", gridTemplateColumns: "150px 160px 1fr 120px 96px", padding: "10px 16px", borderBottom: "1px solid #ececec", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#a8aca6" }}>
              <span>Actor</span><span>Event</span><span>Target</span><span>Client</span><span style={{ textAlign: "right" }}>When</span>
            </div>
            {data.events.map((e) => (
              <div key={e.id} style={{ display: "grid", gridTemplateColumns: "150px 160px 1fr 120px 96px", padding: "10px 16px", borderTop: "1px solid #f4f3ee", fontSize: 12.5, alignItems: "center" }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={e.actor}>
                  {e.actor === "system" ? <span style={{ color: "#8a8e88" }}>system</span> : e.actor.split("@")[0]}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: FAMILY_COLOR[familyOf(e.event)] ?? "#8a8e88", flex: "none" }} />
                  <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.event}</span>
                </span>
                <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                  {e.targetKind && <span style={{ fontSize: 10, color: "#8a8e88", background: "#f0efe9", borderRadius: 4, padding: "1px 6px", flex: "none" }}>{e.targetKind}</span>}
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.targetId ?? "—"}</span>
                  {e.hasDiff && <Icon name="git-branch" size={11} color="#a8aca6" />}
                </span>
                <span style={{ color: "#4a504a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.client ?? <span style={{ color: "#bcbfba" }}>Platform</span>}</span>
                <span style={{ textAlign: "right", color: "#8a8e88", fontSize: 11.5 }} title={e.at}>{ago(e.at)}</span>
              </div>
            ))}
            {data.events.length === 0 && <div style={{ padding: 20, fontSize: 13, color: "#8a8e88" }}>No events for this filter.</div>}
          </div>
        </div>
      </div>
    </Page>
  );
}
